import { useEventCallback } from '@ixigo/iui/utils';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseAsyncListOptions<T, Meta = unknown> {
	/**
	 * The function to load the list.
	 * gets debounced and aborted on new query.
	 * @param query - The query to load the list.
	 * @param opts - The options for the load.
	 */
	load?: (
		query: string,
		opts: { signal: AbortSignal },
	) => Promise<{
		items: T[];
		/** Optional metadata that travels with the items (e.g. section info) */
		meta?: Meta;
	}>;
	/**
	 * The debounce time in milliseconds.
	 * @default 200
	 */
	debounceMs?: number;
	/**
	 * The minimum length of the query to initiate a load.
	 * @default 2
	 */
	minQueryLength?: number;
	/**
	 * Whether to preload the list on mount.
	 * pass any truthy value to preload the list on mount.
	 * if it changes, suggestion will be refetched.
	 */

	// biome-ignore lint/suspicious/noExplicitAny: it's being used as dependency for cache invalidation
	preload?: any;
	/**
	 * The query to preload.
	 */
	preloadQuery?: string;
}

export interface UseAsyncListReturn<T, Meta = unknown> {
	/**
	 * The items in the list.
	 */
	items: T[];
	/**
	 * Metadata that travelled with the currently-shown items.
	 */
	meta?: Meta;
	/**
	 * Whether the list is loading.
	 */
	loading: boolean;
	/**
	 * The current query.
	 */
	query?: string;
	/**
	 * The function to set the query.
	 */
	setQuery: (q?: string) => void; // initiates debounced load and aborts previous
}

export function useAsyncList<T, Meta = unknown>(
	options: UseAsyncListOptions<T, Meta>,
): UseAsyncListReturn<T, Meta> {
	const {
		load: loadProp,
		debounceMs = 200,
		minQueryLength = 1,
		preload,
		preloadQuery = '',
	} = options;

	const [state, setState] = useState<{
		items: T[];
		meta?: Meta;
		loading: boolean;
		preloadData?: T[];
		preloadMeta?: Meta;
		preloadKey?: string;
		query?: string;
	}>({
		items: [],
		loading: false,
	});

	const cancelRef = useRef<{
		controller?: AbortController;
		timer?: ReturnType<typeof setTimeout>;
	}>({});

	const load = useEventCallback(
		({ query, preload }: { query?: string; preload?: boolean }) => {
			if (!loadProp) return;
			const q = query ?? (preload ? preloadQuery : '');
			// Cancel any scheduled run
			const ref = cancelRef.current;
			clearTimeout(ref.timer);
			ref.controller?.abort();
			ref.controller = undefined;

			const canQuery = q.length >= minQueryLength || preload;
			if (!canQuery) {
				// If the query is too short, ensure loading is not shown
				setState((prev) => ({ ...prev, loading: false }));
				return;
			}

			// Indicate loading immediately during debounce to avoid transient empty states
			setState((prev) => ({ ...prev, loading: true }));

			const execute = async () => {
				const controller = new AbortController();
				ref.controller = controller;
				const dataKey = preload ? 'preloadData' : 'items';
				const metaKey = preload ? 'preloadMeta' : 'meta';
				try {
					const res = await loadProp(q, { signal: controller.signal });
					setState((prev) => ({
						...prev,
						[dataKey]: res.items,
						[metaKey]: res.meta,
						preloadKey: String(preload),
						loading: false,
					}));
				} catch {
					if (controller.signal.aborted) return;
					setState((prev) => ({
						...prev,
						[dataKey]: [],
						[metaKey]: undefined,
						preloadKey: String(preload),
						loading: false,
					}));
				}
			};

			if (preload) {
				execute();
				return;
			}

			ref.timer = setTimeout(execute, debounceMs);
		},
	);

	const setQuery = useCallback(
		(q?: string) => {
			setState((prev) => ({ ...prev, query: q, loading: false }));
			// `setQuery(undefined)` is the "reset to idle" signal (e.g. on input
			// blur) — clear the typed-text state and abort any pending/in-flight
			// load, but DON'T fire a new fetch. Otherwise blurring an input
			// triggers an autocomplete request with an empty query.
			if (q === undefined) {
				const ref = cancelRef.current;
				clearTimeout(ref.timer);
				ref.controller?.abort();
				ref.controller = undefined;
				return;
			}
			load({ query: q });
		},
		[load],
	);

	// Preload once on mount if requested
	useEffect(() => {
		load({ preload });
	}, [load, preload]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			const ref = cancelRef.current;
			clearTimeout(ref.timer);
			ref.controller?.abort();
		};
	}, []);

	const preloadActive = String(preload) === state.preloadKey;
	const preloadItems = preloadActive ? (state.preloadData ?? []) : [];
	// `items`: prefer the query results when they exist; otherwise fall back
	// to preload. Critical for click stability — blur resets `state.query` to
	// undefined, but we must NOT immediately swap the rendered items, or the
	// click that triggered the blur won't land on the suggestion the user was
	// aiming at.
	const showingQueryItems = state.items.length > 0;
	// A non-empty typed query that has finished loading and returned zero items.
	// This must win over the preload fallback below: otherwise, when preload is
	// active (the other field's coords), a no-match search would render stale
	// preload rows (e.g. nearby airports) and suppress the BE zero-state message.
	// The query must be non-empty: an empty query is preload-equivalent (focus
	// surfaces nearby suggestions), so an empty result there should still fall
	// back to preload rather than blank the list. Safe for click stability — a
	// zero-result list has no suggestion to click, so swapping to the empty state
	// on blur can't steal a pending click.
	const queryResolvedEmpty =
		!showingQueryItems &&
		state.query !== undefined &&
		state.query.length > 0 &&
		state.query.length >= minQueryLength &&
		!state.loading;
	// Meta pairs with whichever items source we're rendering:
	//   - Query items present → query meta.
	//   - Query resolved empty → query meta (carries the BE `displayMessage`).
	//   - Preload items shown (query still empty/in-flight) → preload meta.
	//     Without this pairing, a fresh query in flight would render preload
	//     items under empty `state.meta` and the section heading pops in late.

	return {
		items: showingQueryItems || queryResolvedEmpty ? state.items : preloadItems,
		meta:
			showingQueryItems || queryResolvedEmpty
				? state.meta
				: preloadActive
					? state.preloadMeta
					: undefined,
		loading: state.loading,
		query: state.query,
		setQuery,
	};
}
